import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agentApi, type Agent } from '@/lib/api';
import { Bot, Plus, Circle, ChevronRight, MessageSquare, Zap, Search } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { PageGuide } from '@/components/PageGuide';

export const Route = createFileRoute('/_auth/agents/')({ component: AgentsPage });

const STATUS_STYLE: Record<string, { dot: string; label: string; bg: string; text: string; border: string }> = {
  ACTIVE:   { dot: '#34d399', label: 'Active',   bg: '#10b98120', text: '#34d399', border: '#10b98140' },
  DRAFT:    { dot: '#64748b', label: 'Draft',    bg: '#47556920', text: '#64748b', border: '#47556940' },
  PAUSED:   { dot: '#fbbf24', label: 'Paused',   bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b40' },
  ARCHIVED: { dot: '#f87171', label: 'Archived', bg: '#ef444420', text: '#f87171', border: '#ef444440' },
};

type StatusFilter = 'ALL' | 'ACTIVE' | 'DRAFT' | 'PAUSED' | 'ARCHIVED';

export default function AgentsPage() {
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: agentApi.list,
  });

  const filtered = agents.filter((a) => {
    if (statusFilter !== 'ALL' && a.status !== statusFilter) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="page">
      <PageGuide pageKey="agents" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Agents</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {isLoading ? '—' : `${filtered.length} agent${filtered.length !== 1 ? 's' : ''}`} · AI agents handling customer conversations
          </p>
        </div>
        <Link
          to="/agents/new"
          id="new-agent-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--color-primary)', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: 'none', boxShadow: '0 2px 12px var(--color-primary-glow)' }}
        >
          <Plus size={15} /> New Agent
        </Link>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
          <input
            id="agents-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents…"
            style={{ width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        {(['ALL', 'ACTIVE', 'DRAFT', 'PAUSED', 'ARCHIVED'] as StatusFilter[]).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: statusFilter === s ? 'var(--color-primary)' : '#1e2130',
            background: statusFilter === s ? 'var(--color-primary-dim)' : 'transparent',
            color: statusFilter === s ? 'var(--color-primary)' : '#64748b',
          }}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── Empty ── */}
      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <Bot size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {search ? `No agents match "${search}"` : 'No agents yet'}
          </p>
          {!search && statusFilter === 'ALL' && (
            <Link to="/agents/new" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, padding: '8px 16px', background: 'var(--color-primary)', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              <Plus size={14} /> Create first agent
            </Link>
          )}
        </div>
      )}

      {/* ── Card grid ── */}
      {!isLoading && filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, marginTop: 20 }}>
          {filtered.map((agent) => <AgentCard key={agent.id} agent={agent} />)}
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent }: { agent: Agent }) {
  const s = STATUS_STYLE[agent.status] ?? STATUS_STYLE.DRAFT;
  return (
    <Link
      to="/agents/$agentId"
      params={{ agentId: agent.id }}
      id={`agent-card-${agent.id}`}
      style={{ textDecoration: 'none', display: 'block', borderRadius: 14, border: '1px solid #1a1f35', background: '#0a0e1c', padding: '18px 20px', transition: 'border-color 0.15s, transform 0.15s', cursor: 'pointer' }}
      className="agentCard"
    >
      {/* card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <Bot size={18} />
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
          <Circle size={6} fill="currentColor" /> {s.label}
        </span>
      </div>

      <h3 style={{ margin: '0 0 5px', fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{agent.name}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
        {agent.description ?? 'No description'}
      </p>

      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#475569' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <MessageSquare size={12} /> {agent._count?.conversations ?? 0} convs
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Zap size={12} /> {agent.routingChannels.length > 0 ? agent.routingChannels.join(', ') : 'All channels'}
        </span>
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #1a1f35', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#475569' }}>
        <span>View details</span>
        <ChevronRight size={14} />
      </div>
    </Link>
  );
}
